import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { configureAmplify } from "./amplify";
import type { Role } from "@/lib/permissions";
import { env } from "@/lib/env";
import { identifyUser, resetAnalytics } from "@/analytics";

export type AuthIdentity = {
  sub: string;
  email: string;
  role: Role | undefined;
  roles: Role[];
  departmentId: string | undefined;
};

type AuthState = {
  status: "loading" | "signed_in" | "signed_out" | "error";
  identity: AuthIdentity | null;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);
const E2E_SIGNED_OUT_KEY = "respai:e2e:signed-out";
const E2E_ROLE_KEY = "respai:e2e:role";

type Claims = {
  sub?: string;
  email?: string;
  "custom:role"?: string;
  "custom:roles"?: string | string[];
  "custom:department_id"?: string;
};

function readIdentity(claims: Claims): AuthIdentity {
  const validRoles: Role[] = ["chief", "respai_admin", "incident_commander", "firefighter"];
  const rawRole = claims["custom:role"] === "admin" ? "chief" : claims["custom:role"];
  const rawRoles = claims["custom:roles"];
  let parsedRoles: unknown[] = [];
  if (typeof rawRoles === "string") {
    try {
      const parsed = JSON.parse(rawRoles || "[]");
      parsedRoles = Array.isArray(parsed) ? parsed : [];
    } catch {
      parsedRoles = [];
    }
  } else if (Array.isArray(rawRoles)) {
    parsedRoles = rawRoles;
  }
  const roles = parsedRoles
    .map((r) => (r === "admin" ? "chief" : r))
    .filter((r): r is Role => validRoles.includes(r as Role));
  const role = validRoles.includes(rawRole as Role) ? (rawRole as Role) : roles[0];
  return {
    sub: claims.sub ?? "",
    email: claims.email ?? "",
    role,
    roles: roles.length > 0 ? roles : role ? [role] : [],
    departmentId: claims["custom:department_id"],
  };
}

function readE2EIdentity(): AuthIdentity | null {
  if (!env.e2eAuthRole) return null;
  const role = (
    typeof window !== "undefined"
      ? window.localStorage.getItem(E2E_ROLE_KEY)
      : null
  ) || env.e2eAuthRole;
  const identity = readIdentity({
    sub: `e2e-${role}`,
    email: `${role}@respai.test`,
    "custom:role": role,
    "custom:department_id": "11111111-1111-4111-8111-111111111111",
  });
  return identity.role ? identity : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<{
    status: AuthState["status"];
    identity: AuthIdentity | null;
    error: string | null;
  }>({
    status: "loading",
    identity: null,
    error: null,
  });

  const refresh = useCallback(async () => {
    const e2eIdentity = readE2EIdentity();
    if (e2eIdentity) {
      const signedOut =
        typeof window !== "undefined" &&
        window.localStorage.getItem(E2E_SIGNED_OUT_KEY) === "1";
      setState({
        status: signedOut ? "signed_out" : "signed_in",
        identity: signedOut ? null : e2eIdentity,
        error: null,
      });
      return;
    }
    try {
      configureAmplify();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload as Claims | undefined;
      if (!payload?.sub) {
        setState({ status: "signed_out", identity: null, error: null });
        return;
      }
      setState({
        status: "signed_in",
        identity: readIdentity(payload),
        error: null,
      });
    } catch (err) {
      setState({
        status: "signed_out",
        identity: null,
        error: err instanceof Error ? err.message : "Auth check failed",
      });
    }
  }, []);

  // Keep analytics identity in lockstep with auth: identify the signed-in admin
  // (so usage events attribute to them) and reset on sign-out (so the next admin
  // on a shared machine starts a clean profile). No-op when analytics is off.
  useEffect(() => {
    if (state.status === "signed_in" && state.identity) {
      identifyUser({
        sub: state.identity.sub,
        email: state.identity.email,
        role: state.identity.role,
        roles: state.identity.roles,
        departmentId: state.identity.departmentId,
      });
    } else if (state.status === "signed_out") {
      resetAnalytics();
    }
  }, [state.status, state.identity]);

  useEffect(() => {
    void refresh();
    const unsub = Hub.listen("auth", ({ payload }) => {
      if (payload.event === "signedIn" || payload.event === "tokenRefresh") {
        void refresh();
      }
      if (payload.event === "signedOut") {
        setState({ status: "signed_out", identity: null, error: null });
      }
    });
    return () => unsub();
  }, [refresh]);

  const signIn = useCallback(async () => {
    const e2eIdentity = readE2EIdentity();
    if (e2eIdentity) {
      window.localStorage.removeItem(E2E_SIGNED_OUT_KEY);
      setState({ status: "signed_in", identity: e2eIdentity, error: null });
      return;
    }
    configureAmplify();
    await signInWithRedirect();
  }, []);

  const signOut = useCallback(async () => {
    if (readE2EIdentity()) {
      window.localStorage.setItem(E2E_SIGNED_OUT_KEY, "1");
      setState({ status: "signed_out", identity: null, error: null });
      return;
    }
    await amplifySignOut();
  }, []);

  const value = useMemo<AuthState>(
    () => ({ ...state, signIn, signOut, refresh }),
    [state, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
